import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/utils/api';
import { Plus, MessageSquare, Megaphone, Calendar, Globe } from 'lucide-react';
import Layout from '@/components/Layout';

const typeIcons = {
  announcement: Megaphone,
  event: Calendar,
  cultural_tip: Globe,
  general: MessageSquare
};

const typeColors = {
  announcement: 'bg-blue-100 text-blue-800',
  event: 'bg-purple-100 text-purple-800',
  cultural_tip: 'bg-green-100 text-green-800',
  general: 'bg-gray-100 text-gray-800'
};

export default function Community() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [formData, setFormData] = useState({
    title: '', title_ar: '', content: '', content_ar: '', post_type: 'general'
  });

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const res = await api.getCommunityPosts();
      setPosts(res.data);
    } catch (error) { console.error('Failed:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createCommunityPost({ ...formData, author_id: user?.id });
      setDialogOpen(false);
      setFormData({ title: '', title_ar: '', content: '', content_ar: '', post_type: 'general' });
      fetchPosts();
    } catch (error) { console.error('Failed:', error); }
  };

  const isArabic = i18n.language === 'ar';
  const filteredPosts = filterType === 'all' ? posts : posts.filter(p => p.post_type === filterType);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '-';

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">{t('community')}</h1>
            <p className="text-gray-500 mt-1">Community feed, announcements, events & cultural tips</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button><Plus className="mr-2 h-4 w-4" /> {t('addPost')}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{t('addPost')}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Post Type</Label>
                  <Select value={formData.post_type} onValueChange={(v) => setFormData({ ...formData, post_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="announcement">{t('announcement')}</SelectItem>
                      <SelectItem value="event">{t('event')}</SelectItem>
                      <SelectItem value="cultural_tip">{t('culturalTip')}</SelectItem>
                      <SelectItem value="general">{t('general')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{t('title')} (EN)*</Label><Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required /></div>
                <div><Label>{t('title')} (AR)</Label><Input value={formData.title_ar} onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })} /></div>
                <div>
                  <Label>{t('content')} (EN)*</Label>
                  <textarea className="w-full border rounded-md p-3 text-sm min-h-[100px]" value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} required />
                </div>
                <div>
                  <Label>{t('content')} (AR)</Label>
                  <textarea className="w-full border rounded-md p-3 text-sm min-h-[100px]" value={formData.content_ar} onChange={(e) => setFormData({ ...formData, content_ar: e.target.value })} />
                </div>
                <Button type="submit" className="w-full">{t('save')}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          {['all', 'announcement', 'event', 'cultural_tip', 'general'].map(type => (
            <Button key={type} variant={filterType === type ? 'default' : 'outline'} size="sm" onClick={() => setFilterType(type)}>
              {type === 'all' ? 'All' : type === 'cultural_tip' ? t('culturalTip') : t(type)}
            </Button>
          ))}
        </div>

        {/* Posts */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : filteredPosts.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-gray-500">No community posts yet. Be the first to share!</CardContent></Card>
        ) : (
          <div className="space-y-4">
            {filteredPosts.map((post) => {
              const Icon = typeIcons[post.post_type] || MessageSquare;
              return (
                <Card key={post.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${typeColors[post.post_type]}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeColors[post.post_type]}`}>
                            {post.post_type === 'cultural_tip' ? t('culturalTip') : t(post.post_type)}
                          </span>
                          <span className="text-xs text-gray-400">{formatDate(post.created_at)}</span>
                          {post.is_pinned && <span className="text-xs text-blue-600 font-medium">Pinned</span>}
                        </div>
                        <h3 className="font-semibold text-lg text-gray-900">
                          {isArabic && post.title_ar ? post.title_ar : post.title}
                        </h3>
                        <p className="text-gray-600 mt-2 whitespace-pre-wrap">
                          {isArabic && post.content_ar ? post.content_ar : post.content}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
